import React from 'react';
import classNames from 'classnames';
import {
  formatAverage,
  formatAverageDelta,
  formatRoundPeriod,
  ratingTone,
  roundLabel,
  SURVEY_RATING_MAX,
  SurveyRoundTrendRow,
} from '../surveyUtils';

interface Props {
  rows: SurveyRoundTrendRow[];
  selectedId: string;
  onSelect: (launchId: string) => void;
}

const MIN_ROUNDS_TO_COMPARE = 2;

const deltaTone = (delta: number | null): string => {
  if (delta === null || delta === 0) return 'is-flat';
  return delta > 0 ? 'is-up' : 'is-down';
};

/** D-A10: the average rating of each round, newest first, to compare rounds. Only the rating is compared. */
export function SurveyRoundTrend({ rows, selectedId, onSelect }: Props): React.ReactElement | null {
  if (rows.filter((row) => row.average !== null).length < MIN_ROUNDS_TO_COMPARE) return null;

  return (
    <section className='survey-results-card survey-round-trend' aria-labelledby='survey-round-trend-title'>
      <header>
        <h2 id='survey-round-trend-title' className='survey-results-card-label'>
          Evolución de la valoración
        </h2>
        <p className='survey-results-card-hint'>Media de cada ronda y cambio respecto a la ronda anterior.</p>
      </header>
      <ol>
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type='button'
              className={classNames('survey-round-trend-row', {
                'is-selected': row.id === selectedId,
                'is-low-sample': row.lowSample,
              })}
              aria-pressed={row.id === selectedId}
              onClick={() => onSelect(row.id)}
            >
              <span className='survey-round-trend-round'>
                <strong>{roundLabel(row.number)}</strong>
                <span>{formatRoundPeriod(row)}</span>
              </span>
              <span className='survey-round-trend-bar-track' aria-hidden>
                {row.average !== null && (
                  <span
                    className={classNames('survey-round-trend-bar', `tone-${ratingTone(row.average)}`)}
                    style={{ width: `${(row.average / SURVEY_RATING_MAX) * 100}%` }}
                  />
                )}
              </span>
              <span className='survey-round-trend-average'>{formatAverage(row.average)}</span>
              <span className={classNames('survey-round-trend-delta', deltaTone(row.delta))}>
                {formatAverageDelta(row.delta)}
              </span>
              <span className='survey-round-trend-meta'>
                <span>{row.ratingCount === 1 ? '1 valoración' : `${row.ratingCount} valoraciones`}</span>
                {row.lowSample && <span className='survey-round-trend-warning'>Pocas respuestas</span>}
                {row.surveyChanged && (
                  <span
                    className='survey-round-trend-warning'
                    title='Las preguntas de esta ronda son distintas de las de la ronda anterior'
                  >
                    <i className='pi pi-info-circle' aria-hidden /> Encuesta cambiada
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
